import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  SidePanel,
  SidePanelFooter,
  SidePanelHeader,
  SidePanelTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Virtual,
} from '@solinkcorp/solink-ui';
import classNames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { Archive, Inbox, Loader2, MailOpen } from 'lucide-react';
import AnalyticsLogger from 'models/AnalyticsLogger';
import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { getLocalizedStr } from 'utils/LocaleUtil';
import {
  NOTIFICATION_UNREAD_COUNT_DISPLAY_LIMIT,
  NOTIFICATIONS_FILTER_BY_STATE,
} from 'vd-constants';
import {
  createFilterCountMemory,
  FILTER_COUNT_INTENT,
  rememberFilterCounts as updateFilterCounts,
} from './notificationFilterCounts';
import { notificationFilterIcon } from './notificationFilterIcons';
import NotificationItem from './NotificationItem';
import './NotificationsPanel.sass';
class NotificationsPanel extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isOperationInProgress: false,
      newNotificationTimestamp: new Date().getTime(),
      selectedNotificationCount: 0,
      selectedNotificationIds: {},
    };
    this.filterCounts = null;
    this.filterCountMemory = createFilterCountMemory();
    // No-selection bulk waits for loading to start before arming globalBulk.
    this.pendingGlobalBulk = null;
  }

  rememberFilterCounts = (notifications, filterBy, isLoading) => {
    const { notificationErrorIndicies = {}, readCutoffDate } = this.props;
    this.filterCounts = updateFilterCounts(this.filterCountMemory, {
      notifications,
      filterBy,
      isLoading,
      readCutoffDate,
      notificationErrorIndicies,
    });
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentDidUpdate(prevProps) {
    const { isLoading, notifications } = this.props;
    const { newNotificationTimestamp } = this.state;
    const latestTime = get(notifications, '[0].created', 0);
    if (latestTime > newNotificationTimestamp) {
      clearTimeout(this.newNotificationDelay);
      clearTimeout(this.newNotificationAnimationDelay);
      this.newNotificationDelay = setTimeout(() => {
        this.setState({ newNotificationTimestamp: latestTime });
        clearTimeout(this.newNotificationAnimationDelay);
        this.newNotificationAnimationDelay = setTimeout(
          () => this.setState({ newNotificationTimestamp: latestTime + 1 }),
          500,
        );
      }, 500);
    }

    // Arm only after the cutoff write starts loading. Cancel never loads, so
    // a pending click does not apply on a later unrelated list change.
    if (this.pendingGlobalBulk && !prevProps.isLoading && isLoading) {
      this.filterCountMemory.globalBulk = this.pendingGlobalBulk;
      this.pendingGlobalBulk = null;
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    clearTimeout(this.scrollIdleTimer);
  }

  onListScroll = (event) => {
    const list = event.currentTarget;
    list.classList.add('is-scrolling');
    clearTimeout(this.scrollIdleTimer);
    this.scrollIdleTimer = setTimeout(() => {
      list.classList.remove('is-scrolling');
    }, 700);
  };

  clearPendingGlobalBulk = () => {
    this.pendingGlobalBulk = null;
    this.filterCountMemory.globalBulk = null;
  };

  onArchive = (index) => {
    const { notifications, onArchive } = this.props;
    const notification = notifications[index];
    this.clearPendingGlobalBulk();
    if (notification?.id) {
      this.filterCountMemory.intents.set(notification.id, FILTER_COUNT_INTENT.archive);
    }
    onArchive(notification);
  };

  onMarkAsRead = (index) => {
    const { notifications, onMarkAsRead } = this.props;
    const notification = notifications[index];
    this.clearPendingGlobalBulk();
    if (notification?.id) {
      this.filterCountMemory.intents.set(notification.id, FILTER_COUNT_INTENT.read);
    }
    onMarkAsRead(notification);
  };

  onView = (index, isVideoPage) => {
    const { isImpersonating, notifications, onView } = this.props;
    const data = notifications[index];

    if (data?.id && !isImpersonating) {
      this.filterCountMemory.intents.set(data.id, FILTER_COUNT_INTENT.read);
    }

    onView(data, isVideoPage);
  };

  handleFilterChange = ({ value }) => {
    const { onFilter, userId } = this.props;
    this.clearPendingGlobalBulk();
    onFilter({ filterBy: value });
    AnalyticsLogger.notificationFilterView({ type: value, userId });
  };

  handleCheckNotification = ({ value, index }) => {
    const { notifications } = this.props;
    const { selectedNotificationCount, selectedNotificationIds: source } = this.state;
    const selectedNotificationIds = { ...source };
    const id = get(notifications, [index, 'id']);
    const isNotificationChecked = selectedNotificationIds[id];
    let count = selectedNotificationCount;

    if (id) {
      if (value && !isNotificationChecked) {
        selectedNotificationIds[id] = true;
        count++;
      } else if (isNotificationChecked) {
        delete selectedNotificationIds[id];
        count--;
      }

      this.setState({ selectedNotificationCount: count, selectedNotificationIds });
    } else {
      console.error('The notification does not have an id!');
    }
  };

  archiveMultiple = async () => {
    const { archiveAll } = this.props;
    const { selectedNotificationIds } = this.state;
    const ids = Object.keys(selectedNotificationIds);
    this.clearPendingGlobalBulk();
    if (ids.length === 0) {
      // Arm in componentDidUpdate when loading starts after confirm.
      this.pendingGlobalBulk = FILTER_COUNT_INTENT.archive;
    } else {
      this.filterCountMemory.bulk = {
        intent: FILTER_COUNT_INTENT.archive,
        ids: new Set(ids),
      };
    }
    this.setState({ isOperationInProgress: true });
    try {
      await archiveAll(selectedNotificationIds);
    } finally {
      this.resetSelection();
    }
  };

  markMultipleAsRead = async () => {
    const { markAllAsRead } = this.props;
    const { selectedNotificationIds } = this.state;
    const ids = Object.keys(selectedNotificationIds);
    this.clearPendingGlobalBulk();
    if (ids.length === 0) {
      this.pendingGlobalBulk = FILTER_COUNT_INTENT.read;
    } else {
      this.filterCountMemory.bulk = {
        intent: FILTER_COUNT_INTENT.read,
        ids: new Set(ids),
      };
    }
    this.setState({ isOperationInProgress: true });
    try {
      await markAllAsRead(selectedNotificationIds);
    } finally {
      this.resetSelection();
    }
  };

  resetSelection = () =>
    this.setState({
      isOperationInProgress: false,
      selectedNotificationCount: 0,
      selectedNotificationIds: {},
    });

  rowRenderer = (data, index) => {
    if (!this._isMounted) {
      return null;
    }

    const {
      filterBy,
      useDarkTheme,
      isImpersonating,
      locale,
      readCutoffDate,
      notificationErrorIndicies,
    } = this.props;
    const { newNotificationTimestamp, selectedNotificationIds } = this.state;

    const disableMarkAsRead =
      isImpersonating ||
      filterBy === NOTIFICATIONS_FILTER_BY_STATE.read ||
      filterBy === NOTIFICATIONS_FILTER_BY_STATE.archived ||
      filterBy === NOTIFICATIONS_FILTER_BY_STATE.error;
    const disableArchive = isImpersonating || filterBy === NOTIFICATIONS_FILTER_BY_STATE.archived;
    const { created, id, state } = data || {};

    const isRead = created < readCutoffDate || state === NOTIFICATIONS_FILTER_BY_STATE.read;
    const isNew = !isRead && created >= newNotificationTimestamp;

    return (
      <NotificationItem
        darkTheme={useDarkTheme}
        hasError={!!notificationErrorIndicies[id]}
        data={data}
        disableArchive={disableArchive}
        disableMarkAsRead={disableMarkAsRead}
        id={id}
        index={index}
        isChecked={selectedNotificationIds[id]}
        isNew={isNew}
        isRead={isRead}
        key={id}
        locale={locale}
        onArchive={this.onArchive}
        onCheck={this.handleCheckNotification}
        onMarkAsRead={this.onMarkAsRead}
        onView={this.onView}
      />
    );
  };

  getActionsDisabledTooltip = () => {
    const { filterBy, isImpersonating, locale } = this.props;

    if (filterBy === NOTIFICATIONS_FILTER_BY_STATE.archived) {
      return locale.notifications.disabledOnArchived;
    } else if (isImpersonating) {
      return locale.notifications.disabledWhenImpersonationg;
    }

    return '';
  };

  handleClose = () => {
    const { onClose } = this.props;

    onClose();
    this.handleFilterChange({ value: NOTIFICATIONS_FILTER_BY_STATE.all });
    this.setState({
      selectedNotificationCount: 0,
      selectedNotificationIds: {},
    });
  };

  renderFooterAction = ({ text, onClick, disabled, tooltip, surfaceVariant, icon: Icon }) => {
    const button = (
      <Button
        type="button"
        variant="outline"
        className="tw w-full justify-center gap-2"
        disabled={disabled}
        onClick={onClick}
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        <span className="truncate">{text}</span>
      </Button>
    );

    if (!tooltip) {
      return button;
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="tw inline-flex w-full">{button}</span>
        </TooltipTrigger>
        <TooltipContent
          variant={surfaceVariant}
          // This tooltip portals to document.body. Keep it above the panel.
          className="z-[1001]"
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    );
  };

  render() {
    const {
      count,
      filterBy,
      filterByOptions,
      isImpersonating,
      isLoading,
      locale,
      notifications,
      showAll,
      useDarkTheme,
    } = this.props;

    const { isOperationInProgress, selectedNotificationCount, selectedNotificationIds } =
      this.state;
    const actingOnAll = isEmpty(selectedNotificationIds);
    const archiveLabel = actingOnAll
      ? locale.notifications.archiveAll
      : `${locale.notifications.archive} ${selectedNotificationCount}`;
    const markAsReadLabel = actingOnAll
      ? locale.notifications.markAllAsRead
      : getLocalizedStr({
          string: locale.notifications.markCountAsRead,
          replacements: { count: selectedNotificationCount },
        });
    const unreadCount =
      count > NOTIFICATION_UNREAD_COUNT_DISPLAY_LIMIT
        ? `${NOTIFICATION_UNREAD_COUNT_DISPLAY_LIMIT}+`
        : count;
    const showEmptyState = !isLoading && isEmpty(notifications);
    const showNotificationList = !isLoading && !isEmpty(notifications);
    const actionsDisabled =
      filterBy === NOTIFICATIONS_FILTER_BY_STATE.archived ||
      isImpersonating ||
      isOperationInProgress;
    const actionsDisabledTooltip = this.getActionsDisabledTooltip();
    const newMessageBadge = `${unreadCount} ${locale.notifications.newPlural}`;
    const surfaceVariant = useDarkTheme ? 'dark' : 'auto';
    const footerAction = {
      disabled: !count || actionsDisabled,
      tooltip: actionsDisabledTooltip,
      surfaceVariant,
    };
    this.rememberFilterCounts(notifications, filterBy, isLoading);

    return (
      <>
        {showAll && (
          <div
            className={classNames(
              'tw fixed inset-0 z-[999]',
              useDarkTheme ? 'bg-black/50' : 'bg-black/10',
            )}
            onClick={this.handleClose}
          />
        )}
        <SidePanel
          open={!!showAll}
          onOpenChange={(open) => {
            if (!open) this.handleClose();
          }}
          side="right"
          size="md"
          className={classNames('notifications-panel tw !fixed inset-y-0 z-[1000] h-dvh', {
            dark: useDarkTheme,
          })}
        >
          <SidePanelHeader className="tw relative shrink-0 items-start gap-4 pb-2 [&_[data-slot=side-panel-close]>svg]:size-6 [&_[data-slot=side-panel-close]]:absolute [&_[data-slot=side-panel-close]]:right-4 [&_[data-slot=side-panel-close]]:top-4">
            <div className="tw flex min-w-0 flex-wrap items-center gap-2 pr-12">
              <SidePanelTitle className="tw font-normal text-foreground-primary heading-display">
                {locale.notifications.notifications}
              </SidePanelTitle>
              {!isLoading && (
                <Badge variant="secondary" size="compact">
                  {newMessageBadge}
                </Badge>
              )}
            </div>
            <Select value={filterBy} onValueChange={(value) => this.handleFilterChange({ value })}>
              <SelectTrigger
                className="tw mt-4 w-full body-md"
                aria-label={locale.notifications.notifications}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent
                position="popper"
                variant={useDarkTheme ? 'dark' : 'light'}
                className="z-[1001]"
              >
                {filterByOptions.map((group, groupIndex) => (
                  <SelectGroup key={group.label ?? groupIndex}>
                    {groupIndex > 0 ? <SelectSeparator /> : null}
                    {(group.options || []).map((option) => {
                      const FilterIcon = notificationFilterIcon(option.value);
                      const optionCount = this.filterCounts?.[option.value];
                      return (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="pr-3 focus:bg-interaction-primary-background-hover focus:text-primary-foreground data-[highlighted]:bg-interaction-primary-background-hover data-[highlighted]:text-primary-foreground [&>span]:w-full [&_[data-slot=select-item-indicator]]:hidden"
                        >
                          <span className="flex w-full flex-row items-center gap-2">
                            <FilterIcon className="size-4 shrink-0 text-current" aria-hidden />
                            <span className="min-w-0 flex-1 truncate">{option.label}</span>
                            {optionCount != null && (
                              <Badge variant="secondary" size="compact">
                                {optionCount}
                              </Badge>
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </SidePanelHeader>
          <div className="tw relative flex min-h-0 flex-1 flex-col px-6 pb-2">
            {(isOperationInProgress || isLoading) && (
              <div className="tw absolute inset-0 z-10 flex items-center justify-center bg-background/60">
                <Loader2 className="tw size-8 animate-spin text-foreground-secondary" />
              </div>
            )}
            {showEmptyState && (
              <div className="tw flex h-full flex-col items-center justify-center gap-2 text-foreground-secondary">
                <Inbox className="tw size-14" />
                <div>{locale.notifications.noNotifications}</div>
              </div>
            )}
            {showNotificationList && (
              <section className="notifications-list-view tw -mr-6 flex min-h-0 flex-1 flex-col">
                <Virtual
                  className="notification-list-scroll h-full min-h-0 pl-0 pr-6"
                  style={{ scrollbarGutter: 'auto' }}
                  gap={8}
                  data={notifications}
                  onScroll={this.onListScroll}
                >
                  {this.rowRenderer}
                </Virtual>
              </section>
            )}
          </div>
          <SidePanelFooter className="tw grid shrink-0 grid-cols-2 gap-3 px-6 py-3">
            {this.renderFooterAction({
              ...footerAction,
              text: archiveLabel,
              icon: Archive,
              onClick: this.archiveMultiple,
            })}
            {this.renderFooterAction({
              ...footerAction,
              text: markAsReadLabel,
              icon: MailOpen,
              onClick: this.markMultipleAsRead,
            })}
          </SidePanelFooter>
        </SidePanel>
      </>
    );
  }
}

NotificationsPanel.defaultProps = {
  filterByOptions: [],
  onFilter: () => {},
};

const ComposedNotificationsPanel = (props) => {
  return <NotificationsPanel {...props} />;
};

export default connect()(ComposedNotificationsPanel);
